import { COUNTRIES } from './countries';

export interface SurveyQuestion {
    type: 'select' | 'multi-select' | 'rating' | 'textarea' | 'text' | 'number' | 'radio';
    label: string;
    key: string;
    placeholder?: string;
    options?: { label: string, value: any }[];
    required?: boolean;
    maxLength?: number;
    dependsOn?: { key: string, value: any };
}

export interface SurveySection {
    title: string;
    questions: SurveyQuestion[];
}

export interface SurveyConfig {
    title: string;
    subtitle: string;
    sections: SurveySection[];
}

export const SURVEY_CONFIG: SurveyConfig = {
    title: "SURVEY.TITLE",
    subtitle: "SURVEY.SUBTITLE",
    sections: [
        {
            title: "SURVEY.SECTIONS.PERSONAL",
            questions: [
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.NATIONALITY',
                    key: 'nacionalidad',
                    placeholder: 'SURVEY.QUESTIONS.NATIONALITY_PH',
                    options: COUNTRIES.map(c => ({ label: c.name, value: c.name })),
                    required: true
                },
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.RESIDENCE',
                    key: 'residencia',
                    placeholder: 'SURVEY.QUESTIONS.RESIDENCE_PH',
                    options: COUNTRIES.map(c => ({ label: c.name, value: c.name })),
                    required: true
                },
                {
                    type: 'number',
                    label: 'SURVEY.QUESTIONS.AGE',
                    key: 'edad',
                    placeholder: 'SURVEY.QUESTIONS.AGE_PH',
                    required: true
                },
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.MARITAL_STATUS',
                    key: 'estado_civil',
                    placeholder: 'SURVEY.QUESTIONS.MARITAL_STATUS_PH',
                    options: [
                        { label: 'SURVEY.OPTIONS.SINGLE', value: 'soltero' },
                        { label: 'SURVEY.OPTIONS.MARRIED', value: 'casado' },
                        { label: 'SURVEY.OPTIONS.DIVORCED', value: 'divorciado' },
                        { label: 'SURVEY.OPTIONS.NO_ANSWER', value: 'ns/nc' }
                    ],
                    required: true
                },
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.GENDER',
                    key: 'genero',
                    placeholder: 'SURVEY.QUESTIONS.GENDER_PH',
                    options: [
                        { label: 'SURVEY.OPTIONS.FEMALE', value: 'mujer' },
                        { label: 'SURVEY.OPTIONS.MALE', value: 'hombre' },
                        { label: 'SURVEY.OPTIONS.OTHER', value: 'otro' },
                        { label: 'SURVEY.OPTIONS.NO_ANSWER', value: 'ns/nc' }
                    ],
                    required: true
                },
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.EMPLOYMENT',
                    key: 'situacion_laboral',
                    placeholder: 'SURVEY.QUESTIONS.EMPLOYMENT_PH',
                    options: [
                        { label: 'SURVEY.OPTIONS.EMPLOYED', value: 'empleado' },
                        { label: 'SURVEY.OPTIONS.STUDENT', value: 'estudiante' },
                        { label: 'SURVEY.OPTIONS.UNEMPLOYED', value: 'desempleado' },
                        { label: 'SURVEY.OPTIONS.RETIRED', value: 'jubilado' }
                    ],
                    required: true
                },
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.SALARY',
                    key: 'rango_salarial',
                    placeholder: 'SURVEY.QUESTIONS.SALARY_PH',
                    options: [
                        { label: 'SURVEY.OPTIONS.SALARY_1', value: '< 20.000€' },
                        { label: 'SURVEY.OPTIONS.SALARY_2', value: '20.001€ - 40.000€' },
                        { label: 'SURVEY.OPTIONS.SALARY_3', value: '40.001€ - 80.000€' },
                        { label: 'SURVEY.OPTIONS.SALARY_4', value: '> 80.000€' }
                    ],
                    required: true
                }
            ]
        },
        {
            title: "SURVEY.SECTIONS.STAY",
            questions: [
                {
                    type: 'select',
                    label: 'SURVEY.QUESTIONS.VISIT_PURPOSE',
                    key: 'motivo_visita',
                    placeholder: 'SURVEY.QUESTIONS.VISIT_PURPOSE_PH',
                    options: [
                        { label: 'SURVEY.OPTIONS.LEISURE', value: 'ocio' },
                        { label: 'SURVEY.OPTIONS.BUSINESS', value: 'negocio' },
                        { label: 'SURVEY.OPTIONS.VISITING', value: 'visita_familiar' },
                        { label: 'SURVEY.OPTIONS.EVENTS', value: 'eventos' },
                        { label: 'SURVEY.OPTIONS.STUDIES', value: 'estudios' },
                        { label: 'SURVEY.OPTIONS.HEALTH', value: 'salud' },
                        { label: 'SURVEY.OPTIONS.TRANSIT', value: 'transito' }
                    ],
                    required: true
                }
            ]
        },
        {
            title: "SURVEY.SECTIONS.OLIVA_INFO",
            questions: [
                {
                    type: 'rating',
                    label: 'SURVEY.QUESTIONS.SATISFACTION',
                    key: 'satisfaccion',
                    required: true
                },
                {
                    type: 'radio',
                    label: 'SURVEY.QUESTIONS.TOURIST_INFO',
                    key: 'uso_info_turistica',
                    options: [
                        { label: 'SURVEY.OPTIONS.YES', value: true },
                        { label: 'SURVEY.OPTIONS.NO', value: false }
                    ],
                    required: true
                },
                {
                    type: 'text',
                    label: 'SURVEY.QUESTIONS.TOURIST_FEEDBACK',
                    key: 'feedback_info_turistica',
                    placeholder: 'SURVEY.QUESTIONS.TOURIST_FEEDBACK_PH',
                    maxLength: 200,
                    required: true,
                    dependsOn: { key: 'uso_info_turistica', value: true }
                }
            ]
        },
        {
            title: "SURVEY.SECTIONS.ITINERARY",
            questions: [
                {
                    type: 'radio',
                    label: 'SURVEY.QUESTIONS.FIRST_FUERTEVENTURA',
                    key: 'primera_vez_fuerteventura',
                    options: [
                        { label: 'SURVEY.OPTIONS.YES', value: true },
                        { label: 'SURVEY.OPTIONS.NO', value: false }
                    ],
                    required: true
                },
                {
                    type: 'radio',
                    label: 'SURVEY.QUESTIONS.FIRST_OLIVA',
                    key: 'primera_vez_la_oliva',
                    options: [
                        { label: 'SURVEY.OPTIONS.YES', value: true },
                        { label: 'SURVEY.OPTIONS.NO', value: false }
                    ],
                    required: true
                },
                {
                    type: 'multi-select',
                    label: 'SURVEY.QUESTIONS.VISITED_WONDERS',
                    key: 'maravillas_visitadas',
                    options: [
                        { label: 'SURVEY.OPTIONS.EVENT', value: 'evento' },
                        { label: 'SURVEY.OPTIONS.BEACH', value: 'playa' },
                        { label: 'SURVEY.OPTIONS.ARCHITECTURE', value: 'arquitectura' },
                        { label: 'SURVEY.OPTIONS.TOWN', value: 'pueblo' }
                    ],
                    required: true
                }
            ]
        },
        {
            title: "SURVEY.SECTIONS.COMMENTS",
            questions: [
                {
                    type: 'textarea',
                    label: 'SURVEY.QUESTIONS.COMMENTS',
                    key: 'comentarios',
                    placeholder: 'SURVEY.QUESTIONS.COMMENTS_PH',
                    maxLength: 200,
                    required: false
                }
            ]
        }
    ]
};
